/**
 * StripePaymentForm — embedded Stripe Elements card form.
 *
 * Mirrors the prop API of PayPalCheckoutButtons so the Donate / Shop /
 * Raffle pages can drop us in alongside (or instead of) PayPal.
 *
 * Props
 *   purpose      "donation" | "shop" | "raffle"
 *   amount       number (USD)
 *   campaignId   string — optional
 *   raffleId     string — required when purpose === "raffle"
 *   cartId       string — required when purpose === "shop"
 *   donorName    string
 *   donorEmail   string
 *   monthly      boolean — if true, creates a Stripe Subscription instead
 *                          of a one-time PaymentIntent
 *   onSuccess    (data) => void
 *   onError      (err) => void
 *
 * UX flow: We create the PaymentIntent (or Subscription's first invoice)
 * on the backend, receive a client_secret, mount Stripe's PaymentElement,
 * and call `stripe.confirmPayment()` on submit. The PaymentElement
 * automatically shows Card / Apple Pay / Google Pay / Link based on the
 * browser and amount.
 */
import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { CreditCard, Loader2, AlertCircle, Lock } from "lucide-react";
import api from "../lib/api";
import { useContentApi } from "../context/ContentContext";
import { detectBrowser } from "../lib/browser";
import { formatApiError } from "../lib/errors";

// Loaded ONCE per publishable key — loadStripe internally caches, but we
// memoize too so the Elements provider isn't torn down on parent re-renders.
const stripePromiseCache = new Map();
const getStripePromise = (pk) => {
    if (!pk) return null;
    if (!stripePromiseCache.has(pk)) stripePromiseCache.set(pk, loadStripe(pk));
    return stripePromiseCache.get(pk);
};

// Loose email shape check — matches what pydantic/email-validator considers
// a recognizable address. Used in two places: (1) deciding whether to send
// donor_email to the backend at all (we'd rather omit a half-typed string),
// and (2) detecting when the email becomes valid so we can re-fire the
// PaymentIntent creation effect.
const isValidEmailStr = (s) => typeof s === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

const StripePaymentForm = ({
    purpose = "donation",
    amount,
    campaignId,
    raffleId,
    raffleEntryId,
    cartId,
    getCartId,
    donorName = "",
    donorEmail = "",
    monthly = false,
    onSuccess,
    onError,
}) => {
    const [config, setConfig] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [intentMeta, setIntentMeta] = useState(null);
    // Resolved cart id — either the static prop or the id returned by getCartId().
    // When purpose="shop" and getCartId is provided, we call it lazily on the
    // first render tick to create/find the pending order and cache the result.
    const [resolvedCartId, setResolvedCartId] = useState(cartId || "");
    const { c } = useContentApi();
    // Stripe-only kill-switch. The legacy master `payments.disabled` is no
    // longer wired into any UI — the per-processor toggle is the source of truth.
    const paymentsDisabled = c("stripe.disabled", "false") === "true";

    // Pull publishable key once
    useEffect(() => {
        if (paymentsDisabled) return;
        api.get("/stripe/config")
            .then((r) => {
                if (!r.data.configured) {
                    setLoadError("Stripe is not configured on the server.");
                }
                setConfig(r.data);
            })
            .catch(() => setLoadError("Could not reach Stripe config endpoint."));
    }, [paymentsDisabled]);

    // Latest donor name/email — captured via ref so the PI creation effect
    // can READ them when it fires (e.g., for monthly subscription customer
    // creation) WITHOUT re-firing on every keystroke. Re-firing on every
    // keystroke caused Stripe Elements to unmount+remount mid-typing, which
    // looked like the form was "slow / choppy / hanging".
    // Latest donor name/email — captured via ref so the PI creation effect
    // can READ them when it fires WITHOUT re-firing on every keystroke.
    // We DO bump `emailReadyTick` once when the email crosses the "valid"
    // threshold so that the PI creation effect runs ONCE more to pick up
    // the now-valid email (e.g., user typed "paul@" first, then ".com").
    const donorNameRef = React.useRef(donorName);
    const donorEmailRef = React.useRef(donorEmail);
    const [emailReadyTick, setEmailReadyTick] = useState(0);
    useEffect(() => { donorNameRef.current = donorName; }, [donorName]);
    useEffect(() => {
        const wasReady = isValidEmailStr(donorEmailRef.current);
        donorEmailRef.current = donorEmail;
        const isReady = isValidEmailStr(donorEmail);
        if (!wasReady && isReady) setEmailReadyTick((t) => t + 1);
    }, [donorEmail]);

    // Track the active PaymentIntent id via ref so the useEffect can decide
    // whether to CREATE a new intent or UPDATE the existing one. Reusing the
    // same intent across amount changes prevents the Stripe dashboard from
    // accumulating "Incomplete" $X.XX rows every time a donor tweaks their
    // amount before paying.
    const activeIntentIdRef = React.useRef(null);

    // Loose email validator — matches what pydantic/email-validator considers
    // a recognizable address shape. Used to decide whether to SEND the email
    // to the backend (we'd rather omit a half-typed string than have pydantic
    // 422 the whole PaymentIntent creation).
    const isValidEmail = isValidEmailStr;

    // Create the PaymentIntent / Subscription whenever amount/campaign/purpose
    // changes. DELIBERATELY does NOT depend on donor_name/donor_email — those
    // are read from refs so typing in the form doesn't re-create the intent
    // (which would cause the Elements remount + freeze).
    // For monthly, we gate creation on the *initial* presence of name+email
    // (checked via the ref) so the Subscription Customer can be created.
    // Debounced 350ms so typing in the custom-amount field doesn't fire a
    // new PI per keystroke (typing "100" would otherwise create $1, $10, $100).
    useEffect(() => {
        if (!config?.configured) return;
        if (!amount || amount <= 0) return;
        if (monthly && (!isValidEmail(donorEmailRef.current) || !donorNameRef.current)) return;
        // Clear any previous error before retrying.
        setLoadError("");
        const handle = setTimeout(async () => {
            const safeEmail = isValidEmail(donorEmailRef.current) ? donorEmailRef.current.trim() : undefined;
            const safeName = (donorNameRef.current || "").trim() || undefined;
            // If we already have a live one-time PI, just update its amount.
            // Subscriptions can't be re-amounted this way — they recreate.
            if (!monthly && activeIntentIdRef.current) {
                api.post("/stripe/payment-intent/update-amount", {
                    payment_intent_id: activeIntentIdRef.current,
                    amount_usd: parseFloat(amount),
                })
                    .then((r) => {
                        // clientSecret stays the same — but Elements is keyed on
                        // it, so passing the same value keeps the form mounted
                        // (no flicker, no remount).
                        setClientSecret(r.data.client_secret);
                        setIntentMeta({ payment_intent_id: r.data.payment_intent_id });
                    })
                    .catch((err) => {
                        // Existing intent likely no longer modifiable — drop the
                        // ref and let the next render create a fresh one.
                        activeIntentIdRef.current = null;
                        // also surface validation issues with the new amount
                        const msg = formatApiError(err);
                        if (msg) setLoadError(msg);
                    });
                return;
            }
            const endpoint = monthly ? "/stripe/subscriptions" : "/stripe/payment-intent";
            // Resolve cart id lazily via getCartId for shop purpose. This creates
            // (or reuses) a pending Order row on the backend so the webhook can
            // finalize the correct row on payment success.
            let effectiveCartId = resolvedCartId || cartId;
            if (purpose === "shop" && !effectiveCartId && typeof getCartId === "function") {
                try {
                    effectiveCartId = await getCartId();
                    setResolvedCartId(effectiveCartId);
                } catch (e) {
                    setLoadError(formatApiError(e, "Couldn't start checkout"));
                    return;
                }
            }
            const body = monthly
                ? { amount_usd: parseFloat(amount), campaign_id: campaignId || undefined, donor_name: safeName, donor_email: safeEmail }
                : {
                      purpose,
                      amount_usd: parseFloat(amount),
                      campaign_id: campaignId || undefined,
                      raffle_id: raffleId || undefined,
                      raffle_entry_id: raffleEntryId || undefined,
                      cart_id: effectiveCartId || undefined,
                      donor_email: safeEmail,
                      donor_name: safeName,
                  };
            api.post(endpoint, body)
                .then((r) => {
                    setClientSecret(r.data.client_secret);
                    setIntentMeta({
                        payment_intent_id: r.data.payment_intent_id,
                        subscription_id: r.data.subscription_id,
                    });
                    activeIntentIdRef.current = r.data.payment_intent_id;
                })
                .catch((err) => {
                    setLoadError(formatApiError(err, "Could not initialize Stripe payment."));
                });
        }, 350);
        return () => clearTimeout(handle);
    }, [config?.configured, amount, monthly, purpose, campaignId, raffleId, raffleEntryId, cartId, resolvedCartId, emailReadyTick]);

    if (paymentsDisabled) return null; // already shown via PayPal's killswitch banner upstream

    if (loadError) {
        return (
            <div className="text-sm text-[#E53935] italic flex items-start gap-2" data-testid="stripe-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{loadError}</span>
            </div>
        );
    }
    if (!config || !config.configured) {
        return (
            <div className="text-sm text-[#4A5568] flex items-center gap-2" data-testid="stripe-loading">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading card form…
            </div>
        );
    }
    if (!clientSecret) {
        return (
            <div className="text-sm text-[#4A5568] flex items-center gap-2" data-testid="stripe-preparing">
                <Loader2 className="w-4 h-4 animate-spin" />
                Preparing secure payment…
            </div>
        );
    }

    const stripePromise = getStripePromise(config.publishable_key);
    const options = { clientSecret, appearance: { theme: "stripe", variables: { colorPrimary: "#E53935", borderRadius: "12px" } } };

    return (
        <div data-testid={`stripe-form-${purpose}${monthly ? "-monthly" : ""}`}>
            {/* CRITICAL: `key={clientSecret}` forces the <Elements> provider to
                fully UNMOUNT + REMOUNT whenever the PaymentIntent changes
                (e.g. donor changes the amount). Without this, Stripe Elements
                stays bound to the FIRST PaymentIntent it ever saw — so even
                though the backend creates a new $10 intent when the donor
                clicks $10, the form would still charge the original $50.
                See: https://stackoverflow.com/q/72440357 — this is a Stripe
                Elements quirk, the official fix is exactly this remount. */}
            <Elements key={clientSecret} options={options} stripe={stripePromise}>
                <InnerForm
                    purpose={purpose}
                    monthly={monthly}
                    paymentIntentId={intentMeta?.payment_intent_id}
                    expectedAmount={parseFloat(amount)}
                    onSuccess={onSuccess}
                    onError={onError}
                />
            </Elements>
        </div>
    );
};

// Wraps PaymentElement with an ETP / ad-blocker detection: if the element
// doesn't fire its `ready` callback within ~5s, we assume the Stripe iframe
// was blocked (Firefox Strict ETP, uBlock, Brave Shields, etc.) and show a
// helpful fallback explaining the workaround. The message is browser-aware
// so the donor sees instructions that match the icon in their address bar.
const PaymentElementWithFallback = () => {
    const [ready, setReady] = useState(false);
    const [blocked, setBlocked] = useState(false);
    const browser = detectBrowser();

    useEffect(() => {
        if (ready) return;
        const t = setTimeout(() => { if (!ready) setBlocked(true); }, 5000);
        return () => clearTimeout(t);
    }, [ready]);

    const fixInstruction = browser.name === "firefox"
        ? "click the shield icon (left of the URL bar) → toggle off Enhanced Tracking Protection for this site → reload"
        : browser.name === "brave"
        ? "click the Brave shield (right of the URL bar) → set Shields to DOWN → reload"
        : "disable strict privacy / tracker-blocking for this site → reload";

    return (
        <>
            <PaymentElement
                options={{
                    layout: { type: "tabs", defaultCollapsed: false },
                    // Only load the Card method. We were defaulting to letting
                    // Stripe show Link, ACH bank, Apple Pay, Google Pay, etc.
                    // — each one mounts its own iframe + does fingerprinting
                    // (WebGL, font lookup, hCaptcha) which on slower machines
                    // makes the form choppy/hang while typing the card number.
                    paymentMethodOrder: ["card"],
                    wallets: { applePay: "never", googlePay: "never" },
                    fields: { billingDetails: { name: "auto", email: "auto" } },
                }}
                onReady={() => { setReady(true); setBlocked(false); }}
                onLoadError={() => setBlocked(true)}
            />
            {blocked && !ready && (
                <div
                    data-testid="stripe-blocked-fallback"
                    className="border border-amber-300 bg-amber-50 rounded-xl p-4 text-sm text-amber-900 space-y-2"
                >
                    <p className="font-bold flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" strokeWidth={2} />
                        Your browser is blocking the card form
                    </p>
                    <p className="leading-relaxed">
                        Stripe&apos;s secure card-entry iframe was blocked by your browser&apos;s privacy settings
                        {browser.name === "firefox" && " (Firefox Strict Enhanced Tracking Protection)"}
                        {browser.name === "brave" && " (Brave Shields)"}.
                    </p>
                    <p className="leading-relaxed">
                        <strong>Two ways to donate right now:</strong>
                    </p>
                    <ul className="list-disc list-outside ml-5 space-y-1">
                        <li>Use the <strong>PayPal / Debit-Credit card</strong> buttons above — they accept the same cards.</li>
                        <li>Or, {fixInstruction}. Your protection stays on everywhere else.</li>
                        <li>Or switch to <a href="https://www.google.com/chrome/" target="_blank" rel="noreferrer" className="underline font-medium">Chrome</a>, <a href="https://www.microsoft.com/edge" target="_blank" rel="noreferrer" className="underline font-medium">Edge</a>, or Safari — Stripe works there by default.</li>
                    </ul>
                </div>
            )}
        </>
    );
};


// Inner form must live INSIDE <Elements> to use useStripe + useElements hooks.
// NOTE: This component CANNOT be a real <form> — Donate.jsx already wraps the
// whole donation block in a <form>, and HTML doesn't allow nested forms
// (React hydration warning, and outer form can hijack the submit). We use a
// <div> with a manual click handler instead.
const InnerForm = ({ purpose, monthly, paymentIntentId, expectedAmount, onSuccess, onError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (!stripe || !elements) return;
        setSubmitting(true);

        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/donate/thanks`,
            },
            redirect: "if_required",
        });

        // Defensive safeguard: confirm the confirmed PaymentIntent's amount
        // matches what the donor expected. The primary defense against the
        // "stale amount" bug is the key={clientSecret} remount on <Elements>
        // above — this is a belt-and-suspenders check in case Stripe ever
        // returns a different-amount PI.
        if (paymentIntent && expectedAmount && !monthly) {
            const charged = paymentIntent.amount / 100;
            if (Math.abs(charged - expectedAmount) > 0.01) {
                toast.error(`Amount mismatch — you selected $${expectedAmount.toFixed(2)} but were charged $${charged.toFixed(2)}. Please contact us for a refund.`);
                // Still call onSuccess so the row gets recorded (and we can refund)
            }
        }

        if (error) {
            toast.error(error.message || "Payment failed");
            onError?.(error);
            setSubmitting(false);
            return;
        }

        if (paymentIntent && paymentIntent.status === "succeeded") {
            try {
                await api.post("/stripe/confirm-payment", { payment_intent_id: paymentIntent.id });
            } catch {
                /* swallow — webhook will finish the job */
            }
            toast.success(monthly ? "Monthly donation set up — thank you!" : "Payment complete — thank you!");
            onSuccess?.({ payment_intent_id: paymentIntent.id, status: paymentIntent.status });
        } else if (paymentIntent) {
            toast.success(`Payment ${paymentIntent.status}`);
            onSuccess?.({ payment_intent_id: paymentIntent.id, status: paymentIntent.status });
        }
        setSubmitting(false);
    };

    return (
        <div className="space-y-4">
            <PaymentElementWithFallback />
            <button
                type="button"
                onClick={handleSubmit}
                disabled={!stripe || submitting}
                data-testid={`stripe-submit-${purpose}${monthly ? "-monthly" : ""}`}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#635BFF] hover:bg-[#4E47CC] text-white rounded-full px-6 py-3.5 font-medium transition-all hover:-translate-y-0.5 disabled:opacity-60"
            >
                {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>
                ) : (
                    <><CreditCard className="w-4 h-4" strokeWidth={1.5} /> {monthly ? "Start monthly donation" : "Pay with card"}</>
                )}
            </button>
            <p className="text-[11px] text-[#4A5568] text-center flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3" strokeWidth={1.5} /> Encrypted by Stripe · card data never touches our servers
            </p>
        </div>
    );
};

export default React.memo(StripePaymentForm);
